import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, forkJoin } from 'rxjs';
import { catchError, map, tap, delay, switchMap } from 'rxjs/operators';
import { ApprovalInstance, ApprovalStepInstance, ResolvedApprovalFlow, ResolvedApprovalStep, InitiatorInfo } from '../models/approval-flow.model';
import { ApprovalFlowDefinition, Role, Department, User, ApprovalStepDefinition, ApiApprovalFlowDefinitionsResponse, ApproverDefinition  } from '../models/approval-flow-definition.model';
import { environment } from '../../../../services/global-environment';
import { AuthService } from '../../../../services/auth.service';
import { I18nService } from '../../../../services/i18n.service';
import { convertLegacyStepToMultiApprover } from '../utils/approval-flow.utils';

@Injectable({
  providedIn: 'root'
})
export class ApprovalService {
  private apiUrl = `${environment.apiUrl}/api/approval`;
  private departmentApiUrl = `${environment.apiUrl}/api/departments`;
  private userApiUrl = `${environment.apiUrl}/api/users`;

  constructor(private http: HttpClient, private authService: AuthService, private i18nService: I18nService) { }


  // 修改: 將 getApprovalInstance 改名為 getApprovalById 以與 ApprovalActionComponent 使用一致
  getApprovalById(approvalId: string): Observable<ApprovalInstance | null> {
    return this.http.get<ApprovalInstance>(`${this.apiUrl}/${approvalId}`).pipe(
      catchError(error => {
        console.error('Error fetching approval instance', error);
        return of(null);
      })
    );
  }

  // 保留原方法名稱以向後兼容
  getApprovalInstance(approvalId: string): Observable<ApprovalInstance | null> {
    return this.getApprovalById(approvalId);
  }

  // 現有方法：獲取表單的簽核狀態
  getApprovalForForm(formId: string): Observable<ApprovalInstance | null> {
    return this.http.get<ApprovalInstance>(`${this.apiUrl}/form/${formId}`).pipe(
      catchError(error => {
        console.error('Error fetching approval for form', error);
        return of(null);
      })
    );
  }

  // 現有方法：執行簽核動作
  performApprovalAction(
    approvalId: string,
    action: 'approve' | 'reject',
    comment?: string,
    signature?: string | null
  ): Observable<ApprovalInstance | null> {
    return this.http.post<ApprovalInstance>(`${this.apiUrl}/${approvalId}/action`, {
      action,
      comment,
      signature
    }).pipe(
      catchError(error => {
        console.error('Error performing approval action', error);
        return of(null);
      })
    );
  }

  // 新增方法：獲取所有簽核流程定義
  getApprovalFlowDefinitions(assistantNo: string): Observable<ApiApprovalFlowDefinitionsResponse> {
    console.log('查詢簽核流程定義，參數:', { assistantNo });

    const params = {
      assistantNo: assistantNo
    };

    return this.http.get<ApiApprovalFlowDefinitionsResponse>(`${this.apiUrl}/flow-definitions`, { params }).pipe(
      map(response => {
        // 處理 API 成功回應
        if (response && response.success && Array.isArray(response.data)) {
          console.log('從 API 成功獲取簽核流程清單，數量:', response.data.length);
          return response;
        }

        console.warn('API 回應格式不正確:', response);
        // 回傳空結果但成功的格式
        return {
          success: true,
          data: [],
          total: 0
        };
      }),
      catchError(error => {
        console.error('獲取簽核流程定義失敗:', error);
        // 回傳失敗結果
        return of({
          success: false,
          data: [],
          total: 0
        });
      })
    );
  }

  // 新增方法：根據ID獲取簽核流程定義
  getApprovalFlowDefinitionById(id: string, assistantNo: string): Observable<ApprovalFlowDefinition | null> {
    console.log('查詢特定簽核流程定義，ID:', id, 'AssistantNo:', assistantNo);

    const params = new HttpParams()
      .set('assistantNo', assistantNo);

    return this.http.get<any>(`${this.apiUrl}/flow-definitions/${id}`, { params }).pipe(
      map(response => {
        // 處理 API 成功回應
        if (response) {
          // 如果回應有 success 和 data 屬性（標準 API 格式）
          if (response.success && response.data) {
            console.log('從 API 成功獲取簽核流程:', response.data);
            return response.data as ApprovalFlowDefinition;
          }
          // 如果回應直接是簽核流程物件
          else if (response.id && response.name) {
            console.log('從 API 成功獲取簽核流程 (直接格式):', response);
            return response as ApprovalFlowDefinition;
          }
        }

        console.warn('API 回應格式不正確:', response);
        return null;
      }),
      catchError(error => {
        console.error('獲取簽核流程定義失敗:', error);
        return of(null);
      })
    );
  }

  // 新增方法：創建新的簽核流程定義
  createApprovalFlowDefinition(flowDef: ApprovalFlowDefinition): Observable<ApprovalFlowDefinition> {
    const { companyid, ...flowDefWithoutCompanyId } = flowDef;
    return this.http.post<any>(`${this.apiUrl}/flow-definitions`, flowDefWithoutCompanyId).pipe(
      map(response => {
        // 處理 API 回應格式: { success: true, data: createdItem, message: "..." }
        if (response && response.success && response.data) {
          return response.data;
        }
        // 如果回應格式不符預期，則返回原始回應
        return response;
      }),
      catchError(error => {
        console.error('Error creating approval flow definition', error);
        throw error;
      })
    );
  }

  // 新增方法：更新簽核流程定義
  updateApprovalFlowDefinition(flowDef: ApprovalFlowDefinition): Observable<ApprovalFlowDefinition> {
    const { companyid, ...flowDefWithoutCompanyId } = flowDef;
    return this.http.put<any>(`${this.apiUrl}/flow-definitions/${flowDef.id}`, flowDefWithoutCompanyId).pipe(
      map(response => {
        // 處理 API 回應格式: { success: true, data: updatedItem, message: "..." }
        if (response && response.success && response.data) {
          return response.data;
        }
        // 如果回應格式不符預期，則返回原始回應
        return response;
      }),
      catchError(error => {
        console.error('Error updating approval flow definition', error);
        throw error;
      })
    );
  }

  // 新增方法：刪除簽核流程定義
  deleteApprovalFlowDefinition(id: string, assistantNo: string): Observable<boolean> {
    // 建立查詢參數
    const params = new URLSearchParams({
      assistantNo: assistantNo
    });

    return this.http.delete<void>(`${this.apiUrl}/flow-definitions/${id}?${params.toString()}`).pipe(
      map(() => true),
      catchError(error => {
        console.error('Error deleting approval flow definition', error);
        throw error;
      })
    );
  }

  // 新增方法：啟動表單的簽核流程
  initiateApprovalFlow(formId: string, flowDefId: string): Observable<ApprovalInstance | null> {
    return this.http.post<ApprovalInstance>(`${this.apiUrl}/initiate`, {
      formId,
      flowDefinitionId: flowDefId
    }).pipe(
      catchError(error => {
        console.error('Error initiating approval flow', error);
        return of(null);
      })
    );
  }

  // 新增方法：解析表單的簽核流程
  resolveApprovalFlowForForm(formName: string, initiatorInfo: InitiatorInfo, assistantNo: string): Observable<ResolvedApprovalFlow> {
    console.log('開始解析簽核流程，表單名稱:', formName, '填寫人資訊:', initiatorInfo, '助手編號:', assistantNo);

    // 使用真實 API 查找適用於此表單的簽核流程
    return this.getApprovalFlowDefinitions(assistantNo).pipe(
      switchMap(response => {
        if (!response.success || !response.data || response.data.length === 0) {
          console.warn('未找到任何簽核流程定義');
          const errorFlow: ResolvedApprovalFlow = {
            flowId: '',
            flowName: this.i18nService.translate('approval.service.no_available_flow'),
            steps: [],
            hasErrors: true,
            errors: [this.i18nService.translate('approval.service.no_flow_found')]
          };
          return of(errorFlow);
        }

        const flows = response.data;
        console.log('從 API 獲取到簽核流程數量:', flows.length);

        // 根據表單名稱查找對應的簽核流程
        const matchedFlow = flows.find(flow =>
          flow.formTypes && flow.formTypes.some(formType =>
            formName.includes(formType) || formType === 'general'
          )
        );

        if (!matchedFlow) {
          // 如果找不到對應的流程，使用預設流程
          const defaultFlow = flows.find(flow => flow.formTypes && flow.formTypes.includes('general')) || flows[0];
          if (defaultFlow) {
            console.log('使用預設簽核流程:', defaultFlow.name);
            return this.resolveFlowSteps(defaultFlow, initiatorInfo, assistantNo);
          } else {
            // 完全沒有可用的流程
            const errorFlow: ResolvedApprovalFlow = {
              flowId: '',
              flowName: this.i18nService.translate('approval.service.no_available_flow'),
              steps: [],
              hasErrors: true,
              errors: [this.i18nService.translate('approval.service.no_applicable_flow')]
            };
            return of(errorFlow);
          }
        }

        console.log('找到匹配的簽核流程:', matchedFlow.name);
        return this.resolveFlowSteps(matchedFlow, initiatorInfo, assistantNo);
      }),
      catchError(error => {
        console.error('解析簽核流程時發生錯誤:', error);
        const errorFlow: ResolvedApprovalFlow = {
          flowId: '',
          flowName: this.i18nService.translate('approval.service.resolution_failed'),
          steps: [],
          hasErrors: true,
          errors: [this.i18nService.translate('approval.service.resolution_error', { error: error.message || error })]
        };
        return of(errorFlow);
      })
    );
  }

  // 新增方法：解析簽核流程的各個步驟
  private resolveFlowSteps(flowDef: ApprovalFlowDefinition, initiatorInfo: InitiatorInfo, assistantNo: string): Observable<ResolvedApprovalFlow> {
    const resolvedFlow: ResolvedApprovalFlow = {
      flowId: flowDef.id || '',
      flowName: flowDef.name,
      steps: [],
      hasErrors: false,
      errors: []
    };

    // 獲取所有必要的資料（部門、角色、使用者）
    return forkJoin({
      departments: this.getDepartments(assistantNo),
      roles: this.getRoles(),
      users: this.getUsers()
    }).pipe(
      map(({ departments, roles, users }) => {
        // 解析每個步驟
        flowDef.steps.forEach((step, index) => {
          try {
            const resolvedStep = this.resolveSingleStep(
              step,
              index + 1,
              initiatorInfo,
              departments,
              roles,
              users
            );
            resolvedFlow.steps.push(resolvedStep);

            if (resolvedStep.error) {
              resolvedFlow.hasErrors = true;
              resolvedFlow.errors.push(resolvedStep.error);
            }
          } catch (error) {
            console.error(`解析步驟 ${index + 1} 時發生錯誤:`, error);
            const errorStep: ResolvedApprovalStep = {
              level: index + 1,
              departid: '',
              roleid: step.roleId || '',
              userid: '',
              stepName: step.name,
              approverName: this.i18nService.translate('approval.service.resolution_failed_simple'),
              isDynamicDepartment: false,
              error: this.i18nService.translate('approval.service.step_resolution_error', { error: String(error) })
            };
            resolvedFlow.steps.push(errorStep);
            resolvedFlow.hasErrors = true;
            resolvedFlow.errors.push(errorStep.error!);
          }
        });

        return resolvedFlow;
      })
    );
  }

  // 新增方法：解析單個簽核步驟（支援多簽核者）
  private resolveSingleStep(
    step: ApprovalStepDefinition,
    level: number,
    initiatorInfo: InitiatorInfo,
    departments: Department[],
    roles: Role[],
    users: User[]
  ): ResolvedApprovalStep {
    // 確保步驟有 approvers 陣列（向下相容）
    const convertedStep = convertLegacyStepToMultiApprover(step);

    const resolvedStep: ResolvedApprovalStep = {
      level: level,
      departid: '',
      roleid: '',
      userid: '',
      stepName: convertedStep.name,
      approverName: this.i18nService.translate('approval.service.pending_resolution'),
      isDynamicDepartment: false,
      approverList: [] // 初始化簽核者列表
    };

    try {
      console.log('解析步驟:', convertedStep.name, '簽核者數量:', convertedStep.approvers.length);

      // 遍歷所有簽核者
      for (const approver of convertedStep.approvers) {
        const resolvedApprovers = this.resolveApprover(
          approver,
          initiatorInfo,
          departments,
          roles,
          users
        );

        // 將解析結果加入 approverList
        resolvedStep.approverList!.push(...resolvedApprovers);
      }

      console.log('解析完成，簽核者列表:', resolvedStep.approverList);

      // 設定向下相容的欄位（使用第一個簽核者）
      if (resolvedStep.approverList!.length > 0) {
        const firstApprover = resolvedStep.approverList![0];
        resolvedStep.userid = firstApprover.id;
        resolvedStep.approverName = firstApprover.name;
        resolvedStep.departid = firstApprover.departmentId || '';
        resolvedStep.roleid = firstApprover.roleId || '';
        resolvedStep.isDynamicDepartment = firstApprover.isDynamicDepartment || false;
      } else {
        // 沒有找到任何簽核者
        resolvedStep.error = this.i18nService.translate('approval.service.no_approvers_found');
        resolvedStep.approverName = this.i18nService.translate('approval.service.approver_not_found_simple');
      }

      // 如果有多個簽核者，更新顯示名稱
      if (resolvedStep.approverList!.length > 1) {
        const names = resolvedStep.approverList!.map(a => a.name).join(', ');
        resolvedStep.approverName = this.i18nService.translate('approval.service.multiple_approvers', {
          count: resolvedStep.approverList!.length,
          names: names
        });
      }

    } catch (error) {
      console.error('解析步驟時發生錯誤:', error);
      resolvedStep.error = this.i18nService.translate('approval.service.resolution_exception', { error: String(error) });
      resolvedStep.approverName = this.i18nService.translate('approval.service.resolution_failed_simple');
    }

    return resolvedStep;
  }

  // 新增私有方法：解析單個簽核者定義
  private resolveApprover(
    approver: ApproverDefinition,
    initiatorInfo: InitiatorInfo,
    departments: Department[],
    roles: Role[],
    users: User[]
  ): any[] {
    const resolvedApprovers: any[] = [];

    try {
      if (approver.type === 'specific_user') {
        // 特定使用者
        if (approver.userId) {
          const user = users.find(u => u.id === approver.userId);
          if (user) {
            resolvedApprovers.push({
              id: user.id,
              name: user.name,
              email: user.email,
              departmentId: user.departmentId,
              roleId: user.roleId,
              type: 'specific_user'
            });
            console.log('解析特定使用者:', user.name);
          } else {
            console.warn('找不到使用者:', approver.userId);
          }
        }
      } else if (approver.type === 'role_based') {
        // 基於角色的簽核人
        if (!approver.roleId) {
          console.warn('角色 ID 未設定');
          return resolvedApprovers;
        }

        // 解析目標部門
        let targetDepartmentId = approver.departmentId || '';
        let isDynamicDepartment = approver.isDynamicDepartment || false;

        if (isDynamicDepartment) {
          switch (approver.departmentId) {
            case 'submitter_dept':
              targetDepartmentId = initiatorInfo.departmentId;
              console.log('動態部門 - 跟填寫人同部門:', targetDepartmentId);
              break;
            case 'parent_dept':
              // 查找填寫人部門的上級部門
              const parentDept = departments.find(d => d.id === initiatorInfo.departmentId + '_parent');
              targetDepartmentId = parentDept ? parentDept.id : initiatorInfo.departmentId;
              console.log('動態部門 - 填寫人的上級部門:', targetDepartmentId);
              break;
            default:
              targetDepartmentId = approver.departmentId || '';
          }
        }

        // 查找所有符合條件的使用者
        const matchedUsers = users.filter(u =>
          u.roleId === approver.roleId &&
          (targetDepartmentId === '' || u.departmentId === targetDepartmentId)
        );

        console.log('基於角色查找到的使用者數量:', matchedUsers.length);

        // 將所有符合條件的使用者加入列表
        matchedUsers.forEach(user => {
          resolvedApprovers.push({
            id: user.id,
            name: user.name,
            email: user.email,
            departmentId: user.departmentId,
            roleId: user.roleId,
            type: 'role_based',
            isDynamicDepartment: isDynamicDepartment
          });
        });

        if (matchedUsers.length === 0) {
          console.warn('沒有找到符合角色的簽核者:', {
            roleId: approver.roleId,
            departmentId: targetDepartmentId
          });
        }
      }
    } catch (error) {
      console.error('解析簽核者時發生錯誤:', error);
    }

    return resolvedApprovers;
  }

  // 新增方法：解析動態簽核人（為向後相容保留的方法）
  resolveDynamicApprover(step: ApprovalStepDefinition, initiatorInfo: InitiatorInfo, assistantNo: string): Observable<ResolvedApprovalStep> {
    return forkJoin({
      departments: this.getDepartments(assistantNo),
      roles: this.getRoles(),
      users: this.getUsers()
    }).pipe(
      map(({ departments, roles, users }) => {
        return this.resolveSingleStep(step, 1, initiatorInfo, departments, roles, users);
      })
    );
  }

  // 新增方法：取得部門資料
  getDepartments(assistantNo: string): Observable<Department[]> {
    console.log('查詢部門資料，參數:', { assistantNo });

    return this.http.get<any>(`${this.departmentApiUrl}/${assistantNo}`).pipe(
      map(response => {
        // 處理 API 成功回應
        if (response && response.success && response.data) {
          console.log('從 API 成功獲取部門資料:', response.data);

          // 將樹狀結構轉換為平面的部門陣列
          const departments = this.extractDepartmentsFromTree(response.data);
          console.log('轉換後的部門陣列:', departments);
          return departments;
        }

        console.warn('API 回應格式不正確或無資料:', response);
        return [];
      }),
      catchError(error => {
        console.error('獲取部門資料失敗:', error);
        return of([]);
      })
    );
  }

  // 私有方法：從樹狀結構中提取所有部門
  private extractDepartmentsFromTree(data: any): Department[] {
    const departments: Department[] = [];

    // 遞迴函數來提取部門
    const extractDepartments = (node: any, parentId?: string, level: number = 0) => {
      if (node.type === 'department') {
        const department: Department = {
          id: node.id,
          name: node.name,
          level: level,
          parentId: parentId
        };
        departments.push(department);
      }

      // 如果是公司根節點，也視為部門
      if (node.type === 'company') {
        const department: Department = {
          id: node.id,
          name: node.name,
          level: level,
          parentId: parentId
        };
        departments.push(department);
      }

      // 遞迴處理子節點
      if (node.children && Array.isArray(node.children)) {
        node.children.forEach((child: any) => {
          extractDepartments(child, node.type === 'department' || node.type === 'company' ? node.id : parentId, level + 1);
        });
      }
    };

    extractDepartments(data);
    return departments;
  }

  // 新增方法：取得角色資料
  getRoles(): Observable<Role[]> {
    console.log('取得角色資料從 AuthService');

    // 從 AuthService 取得角色選項
    const roleOptions = this.authService.roleOptions;

    // 將 RoleOption 轉換為 Role 格式
    const roles: Role[] = roleOptions.map(option => ({
      id: option.value,
      name: option.label,
      departmentId: null, // 角色不綁定特定部門
      isCommonRole: true  // 標記為通用角色
    }));

    console.log('轉換後的角色資料:', roles);
    return of(roles);
  }

  // 新增方法：取得使用者資料
  getUsers(): Observable<User[]> {
    console.log('查詢使用者資料');

    return this.http.get<any>(`${this.userApiUrl}/getusers`).pipe(
      map(response => {
        // 處理 API 成功回應
        if (response && response.success && response.data && Array.isArray(response.data)) {
          console.log('從 API 成功獲取使用者資料，數量:', response.data.length);

          // 將 API 回應轉換為 User 格式
          const users: User[] = response.data.map((user: any) => ({
            id: user.id,
            name: user.username || user.email || 'Unknown User',
            departmentId: user.departmentId || null, // API 可能沒有這個欄位
            email: user.email || '',
            position: user.position || ''
          }));

          console.log('轉換後的使用者資料:', users);
          return users;
        }

        console.warn('API 回應格式不正確或無資料:', response);
        return [];
      }),
      catchError(error => {
        console.error('獲取使用者資料失敗:', error);
        return of([]);
      })
    );
  }

  // 新增方法：提交簽核結果並更新表單資料
  submitApprovalAndUpdateForm(formData: {
    id: string;
    stepIndex: number;
    approvalData: any;
  }): Observable<any> {
    const { id, stepIndex, approvalData } = formData;

    console.log('提交簽核結果並更新表單資料:', {
      id,
      stepIndex,
      approvalData
    });

    // 呼叫新的 API 來更新簽核流程步驟
    return this.http.put<any>(`${environment.apiUrl}/api/form-manager/update_approval_steps`, {
      id,
      stepIndex,
      approvalData
    }).pipe(
      map(response => {
        console.log('簽核流程步驟更新成功:', response);
        return response;
      }),
      catchError(error => {
        console.error('提交簽核結果失敗:', error);
        throw error;
      })
    );
  }

  /**
   * 檢查操作模式是否應該觸發簽核流程
   * @param isFormalSave 是否為正式存檔
   * @param isDraftMode 是否為暫存模式
   * @returns 是否應該觸發簽核流程
   */
  shouldTriggerApprovalFlow(isFormalSave: boolean, isDraftMode: boolean): boolean {
    // 只有在正式存檔且非暫存模式時才觸發簽核流程
    const shouldTrigger = isFormalSave && !isDraftMode;
    console.log('ApprovalService: 檢查是否應該觸發簽核流程', {
      isFormalSave,
      isDraftMode,
      shouldTrigger
    });
    return shouldTrigger;
  }

  /**
   * 檢查操作模式是否應該發送通知
   * @param isFormalSave 是否為正式存檔
   * @param isDraftMode 是否為暫存模式
   * @returns 是否應該發送通知
   */
  shouldSendNotification(isFormalSave: boolean, isDraftMode: boolean): boolean {
    // 只有在正式存檔且非暫存模式時才發送通知
    const shouldSend = isFormalSave && !isDraftMode;
    console.log('ApprovalService: 檢查是否應該發送通知', {
      isFormalSave,
      isDraftMode,
      shouldSend
    });
    return shouldSend;
  }

  // // 新增方法：取得表單完整資料（包含簽核結果）
  // getFormWithApprovalStatus(id: string, companyid: string): Observable<any> {
  //   console.log('取得表單完整資料，包含簽核狀態:', { id, companyid });

  //   const params = new HttpParams().set('companyid', companyid);

  //   return this.http.get<any>(`${environment.apiUrl}/api/forms/${id}`, { params }).pipe(
  //     map(response => {
  //       console.log('成功取得表單資料:', response);
  //       return response;
  //     }),
  //     catchError(error => {
  //       console.error('取得表單資料失敗:', error);
  //       return of(null);
  //     })
  //   );
  // }
}
